import type {Token} from '../token';

type MediaInput = string | Token.Value<string>;
type MediaLiteral<TQuery extends string = 'query'> = `@media ${TQuery}`;

type StringifiedMediaInput<T extends MediaInput> = T extends string
  ? T
  : Token.Path<T>;

type TrimMediaQuery<T extends string> = T extends `@media ${infer U}` ? U : T;

type MediaUpQuery<TValue extends MediaInput> =
  `(min-width: ${StringifiedMediaInput<TValue>})`;

type MediaDownQuery<TValue extends MediaInput> =
  `(max-width: calc(${StringifiedMediaInput<TValue>} - 0.1px))`;

type MediaBetweenQuery<
  TMin extends MediaInput,
  TMax extends MediaInput,
> = `(${MediaUpQuery<TMin>} and ${MediaDownQuery<TMax>})`;

/**
 * A helper for constructing media queries using Sail's design tokens.
 *
 * @see https://sail.stripe.me/apis/media
 */
export const media = {
  /**
   * Used to wrap any arbitrary media query conditions.
   *
   * @param value  - Accepts a string.
   */
  query<TSelector extends string>(
    value: TSelector,
  ): MediaLiteral<TrimMediaQuery<TSelector>> {
    return `@media ${value.replace(/@media /g, '')}` as MediaLiteral<
      TrimMediaQuery<TSelector>
    >;
  },
  /**
   * Used to target devices with a screen width equal to, or greater than the provided breakpoint value.
   *
   * Example: `media.up('500px')` matches `[500px, ∞)`.
   *
   * @param value - Accepts a string or a [viewport token](https://sail.stripe.me/ui/tokens/viewport).
   */
  up<TValue extends MediaInput>(
    value: TValue,
  ): MediaLiteral<MediaUpQuery<TValue>> {
    return media.query(`(min-width: ${value})`) as MediaLiteral<
      MediaUpQuery<TValue>
    >;
  },
  /**
   * Used to target devices with a screen width smaller than the provided breakpoint value (exclusive).
   *
   * Example: `media.down('500px')` matches `[0, 500px)`.
   *
   * @param value - Accepts a string or a [viewport token](https://sail.stripe.me/ui/tokens/viewport).
   */
  down<TValue extends MediaInput>(
    value: TValue,
  ): MediaLiteral<MediaDownQuery<TValue>> {
    return media.query(`(max-width: calc(${value} - 0.1px))`) as MediaLiteral<
      MediaDownQuery<TValue>
    >;
  },
  /**
   * Used to target devices with a screen width between the two provided breakpoint values.
   *
   * Example: `media.between('500px', '1000px')` matches `[500px, 1000px)`.
   *
   * @param min - Accepts a string or a [viewport token](https://sail.stripe.me/ui/tokens/viewport).
   * @param max - Accepts a string or a [viewport token](https://sail.stripe.me/ui/tokens/viewport).
   */
  between<TMin extends MediaInput, TMax extends MediaInput>(
    min: TMin,
    max: TMax,
  ): MediaLiteral<MediaBetweenQuery<TMin, TMax>> {
    return media.query(
      `(${media.up(min)} and ${media.down(max)})`,
    ) as MediaLiteral<MediaBetweenQuery<TMin, TMax>>;
  },
};
